import { ChangeDetectionStrategy, Component, computed, inject, output, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { firstValueFrom } from 'rxjs';
import { AuthService } from '../../../../core/auth/auth.service';
import { CalendarPortion, Role } from '../../../../models/enums';
import { Calendar, CalendarLocalisation } from '../../../../models/interfaces';
import { NotificationsService } from '../../../../services/notifications.service';
import { LiquidDeleteService } from '../../../../services/liquid-delete.service';
import { UtilsService } from '../../../../services/utils.service';
import { PORTION_OPTIONS } from '../../constants/portion-options';
import { CalendarCrudService } from '../../services/calendar-crud.service';
import { CalendarDataService } from '../../services/calendar-data.service';
import { findOverlap, isPresenceOverlapError, PRESENCE_OVERLAP_MESSAGE } from '../../utils/presence-slots';

/**
 * Contenu du popover de saisie rapide de présence, ouvert depuis la sidenav
 * globale. Le parent (sidenav) fournit l'enveloppe `<app-popover>` : ce
 * composant n'a ni backdrop ni positionnement, uniquement le formulaire.
 */
@Component({
  selector: 'app-quick-presence-popover',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [FormsModule],
  templateUrl: './quick-presence-popover.component.html',
  styleUrl: './quick-presence-popover.component.scss',
})
export class QuickPresencePopoverComponent {
  private auth = inject(AuthService);
  private crudService = inject(CalendarCrudService);
  private calendarData = inject(CalendarDataService);
  private notificationsService = inject(NotificationsService);
  private utilsService = inject(UtilsService);
  private liquid = inject(LiquidDeleteService);

  created = output<void>();
  dismissed = output<void>();

  protected readonly CalendarPortion = CalendarPortion;
  protected readonly portionOptions = PORTION_OPTIONS;

  #calendars = signal<Calendar[]>(this.auth.profile?.organisation?.calendars ?? []);
  calendars = computed(() => this.#calendars());
  showCalendarSelect = computed(() => this.calendars().length > 1);

  #calendarId = signal<number | null>(this.calendars()[0]?.id ?? null);
  calendarId = computed(() => this.#calendarId());

  localisations = computed<CalendarLocalisation[]>(() =>
    this.calendars().find(c => c.id === this.calendarId())?.localisations ?? []
  );
  showLocalisationSelect = computed(() => this.localisations().length > 1);

  #localisationId = signal<number | null>(null);
  localisationId = computed(() => this.#localisationId());

  #date = signal<string>(this.utilsService.convertDateForSupabase(new Date()));
  date = computed(() => this.#date());

  #portion = signal<CalendarPortion>(this.auth.profile?.preferred_calendar_portion ?? CalendarPortion.FullDay);
  portion = computed(() => this.#portion());
  isCustomPortion = computed(() => this.portion() === CalendarPortion.Custom);

  #customPortion = signal('');
  customPortion = computed(() => this.#customPortion());

  #prefilling = signal(true);
  #submitting = signal(false);
  loading = computed(() => this.#submitting());

  #conflictError = signal<string | null>(null);
  conflictError = computed(() => this.#conflictError());

  canSubmit = computed(() => {
    if (this.#prefilling() || this.#submitting()) return false;
    if (!this.calendarId()) return false;
    return !this.isCustomPortion() || this.customPortion().trim().length > 0;
  });

  constructor() {
    void this.prefillFromLastPresence();
  }

  /**
   * Cascade de préremplissage (#171) : dernière saisie du profil en priorité,
   * puis préférences du profil / première option disponible.
   */
  private async prefillFromLastPresence(): Promise<void> {
    const profile = this.auth.profile;
    if (!profile) {
      this.#prefilling.set(false);
      return;
    }

    const last = await firstValueFrom(this.crudService.getLastPresenceForProfile(profile.id)).catch(() => null);
    const calendars = this.calendars();

    const calendarId = calendars.find(c => c.id === last?.calendar_id)?.id ?? calendars[0]?.id ?? null;
    this.#calendarId.set(calendarId);

    const localisations = calendars.find(c => c.id === calendarId)?.localisations ?? [];
    const lastLocalisation = localisations.find(l => l.id === last?.localisation_id);
    const localisationId = lastLocalisation?.id
      ?? (localisations.length === 1 ? localisations[0].id : localisations[0]?.id)
      ?? null;
    this.#localisationId.set(localisationId ?? null);

    const portion = last?.portion ?? profile.preferred_calendar_portion ?? CalendarPortion.FullDay;
    this.#portion.set(portion);
    if (portion === CalendarPortion.Custom) {
      this.#customPortion.set(last?.custom_portion ?? '');
    }

    this.#prefilling.set(false);
  }

  setDate(value: string): void {
    this.#date.set(value);
    this.#conflictError.set(null);
  }

  setCalendar(id: number): void {
    this.#calendarId.set(id);
    const localisations = this.localisations();
    this.#localisationId.set(localisations[0]?.id ?? null);
    this.#conflictError.set(null);
  }

  setLocalisation(id: number): void {
    this.#localisationId.set(id);
  }

  setPortion(value: CalendarPortion): void {
    this.#portion.set(value);
    this.#conflictError.set(null);
    if (value !== CalendarPortion.Custom) {
      this.#customPortion.set('');
    }
  }

  setCustomPortion(value: string): void {
    this.#customPortion.set(value);
  }

  async submit(): Promise<void> {
    const profile = this.auth.profile;
    const calendarId = this.calendarId();
    if (!profile || !calendarId || !this.canSubmit()) return;

    this.#conflictError.set(null);
    this.#submitting.set(true);

    try {
      const hasConflict = await this.checkConflict(profile.id, calendarId);
      if (hasConflict) {
        this.#conflictError.set(PRESENCE_OVERLAP_MESSAGE);
        return;
      }

      const customName = profile.preferred_calendar_name || `${profile.first_name} ${profile.last_name}`;
      const portion = this.portion();

      const created = await firstValueFrom(this.crudService.createPresence({
        profile_id: profile.id,
        calendar_id: calendarId,
        date_presence: this.date(),
        portion,
        localisation_id: this.localisationId(),
        custom_portion: portion === CalendarPortion.Custom ? this.customPortion() : undefined,
        custom_name: customName,
      }));

      this.notificationsService.showSuccess('Présence ajoutée');
      // Si le calendrier affiché la montre, la pastille tombe en goutte au rechargement ci-dessous.
      if (created?.id) this.liquid.armCreate(`presence:${created.id}`);

      // Ne jamais setter calendarActif ici : son setter reset la matrice de présences.
      if (this.calendarData.calendarActif?.id === calendarId) {
        await this.calendarData.getPresences(this.calendarData.selectedDate());
      }

      this.created.emit();
    } catch (error) {
      // La base refuse aussi un chevauchement saisi entre-temps (autre onglet, autre appareil).
      if (isPresenceOverlapError(error)) {
        this.#conflictError.set(PRESENCE_OVERLAP_MESSAGE);
      } else {
        this.notificationsService.showError('Impossible d\'ajouter la présence : réessayez dans un instant.');
      }
    } finally {
      this.#submitting.set(false);
    }
  }

  private async checkConflict(profileId: string, calendarId: number): Promise<boolean> {
    const existing = await firstValueFrom(
      this.crudService.getPresencesForSlot(profileId, this.date(), calendarId)
    );
    // Même règle que la base (MOKA-368) : plages horaires qui se recouvrent, horaires
    // personnalisés compris. L'admin calendrier n'y est pas soumis.
    if (this.auth.hasRoleOrSuperAdmin([Role.CalendarsManager])) return false;
    return !!findOverlap(existing, this.portion(), this.customPortion());
  }
}
